/* Next */
import { GetStaticProps } from 'next'

/* Template fallback */
import LegacyHome from 'template/pages/Home/Home'

/* Components */
import LandingPage from 'src/pageBuilder/LandingPage'

/* Types */
import { MenuPageBuilder, isMenuPageBuilderActive } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import { tProductPreview } from 'src/shared/frontendTypes/product'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Functions */
import { post } from 'src/functions/fetch'
import {
  extractProductInquirySettings,
  extractSitePageBuilder,
} from 'src/pageBuilder/sitePageBuilder'

/* Options */
import config from 'src/config'

interface Props {
  title?: string
  description?: string
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  products?: tProductPreview[]
  productPreviewsByKey?: Record<string, tProductPreview>
  count?: number
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const collectProductKeys = (pageBuilder?: MenuPageBuilder) => {
  const keys = new Set<string>()

  for (const block of pageBuilder?.blocks || []) {
    if (block.type !== 'products') continue

    for (const key of block.productKeys || []) {
      const trimmed = key.trim()
      if (trimmed) keys.add(trimmed)
    }
  }

  return [...keys]
}

const getProductPreviewsByKey = async (
  pageBuilder?: MenuPageBuilder
): Promise<Record<string, tProductPreview> | undefined> => {
  const keys = collectProductKeys(pageBuilder)
  if (keys.length === 0) return undefined

  const data = await post({
    url: 'products',
    body: {
      ...config.shop,
      size: keys.length,
      keys,
    },
    local: true,
  })

  if (!isFilterProductsResponse(data)) return undefined

  const previewsByKey: Record<string, tProductPreview> = {}
  for (const product of data.filterProducts.products || [])
    previewsByKey[product.key] = product

  return Object.keys(previewsByKey).length > 0 ? previewsByKey : undefined
}

const HomePage: React.FC<Props> = props => {
  const pageBuilder = isMenuPageBuilderActive(props.pageBuilder)
    ? props.pageBuilder
    : props.sitePageBuilder

  if (!isMenuPageBuilderActive(pageBuilder)) return <LegacyHome />

  return <LandingPage
    {...{
      ...props,
      pageBuilder,
      canonicalUrl: '',
    }}
  />
}

export const getStaticProps: GetStaticProps<Props> = async () => {
  try {
    const data = await post({
      url: 'products',
      body: {
        ...config.shop,
        homePage: true,
      },
      local: true,
    })

    if (!isFilterProductsResponse(data)) return { props: {} }

    const sitePageBuilder = extractSitePageBuilder(data)
    const productInquiry = extractProductInquirySettings(data)
    const productPreviewsByKey = await getProductPreviewsByKey(data.menu?.pageBuilder)

    return {
      props: {
        ...(data.menu?.titel ? { title: data.menu.titel } : {}),
        ...(data.menu?.description ? { description: data.menu.description } : {}),
        ...(data.menu?.metaTitle ? { metaTitle: data.menu.metaTitle } : {}),
        ...(data.menu?.metaDescription ? { metaDescription: data.menu.metaDescription } : {}),
        ...(data.menu?.metaKeywords ? { metaKeywords: data.menu.metaKeywords } : {}),
        ...(data.menu?.pageBuilder ? { pageBuilder: data.menu.pageBuilder } : {}),
        ...(sitePageBuilder ? { sitePageBuilder } : {}),
        ...(productInquiry ? { productInquiry } : {}),
        ...(data.landingPageImageAiGenerated
          ? { landingPageImageAiGenerated: data.landingPageImageAiGenerated }
          : {}),
        ...(productPreviewsByKey ? { productPreviewsByKey } : {}),
        count: data.filterProducts.count || 0,
        products: data.filterProducts.products || [],
      },
      revalidate: 60,
    }
  } catch {
    return {
      props: {},
      revalidate: 60,
    }
  }
}

export default HomePage
